import React, { useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, User, Heart, Pill, FileText, Calendar, Users,
  Lock, Clock, Globe, Volume2, VolumeX, AlertTriangle, Printer, Download, FileImage
} from 'lucide-react';
import PrintableHealthReport from '../components/PrintableHealthReport';
import { useReactToPrint } from 'react-to-print';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DoctorPortalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language, toggleLanguage, speak, stopSpeaking } = useLanguage();
  
  const [accessCode, setAccessCode] = useState(searchParams.get('code') || '');
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  
  const printRef = useRef();
  
  const handlePrintReport = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `EmergencyCard_Health_Report_${patientData?.patient?.name || 'Patient'}`,
  });

  const handleValidate = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      toast.error('Please enter access code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/doctor-access/validate?access_code=${accessCode}`);
      setPatientData(response.data);
      setValidated(true);
      toast.success('Access validated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleSpeak = (text) => {
    if (isSpeaking) {
      stopSpeaking();
      setIsSpeaking(false);
    } else {
      speak(text);
      setIsSpeaking(true);
    }
  };

  const handlePrint = () => {
    setShowPrintDialog(true);
  };
  
  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API_URL}/reports/download/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  if (!validated) {
    return (
      <div className="min-h-screen bg-[#FDFCF8]">
        {/* Header */}
        <header className="secure-header text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8" />
              <div>
                <h1 className="text-xl font-bold font-['Manrope']">{t('appName')}</h1>
                <p className="text-xs opacity-80">{t('doctorPortal')}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-white hover:bg-white/20 lang-toggle"
              data-testid="portal-language-toggle"
            >
              <Globe className="h-5 w-5 mr-2" />
              {language === 'en' ? 'हिंदी' : 'English'}
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex items-center justify-center p-4 min-h-[calc(100vh-80px)]">
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#0D2340]/10 flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-[#0D2340]" />
              </div>
              <CardTitle className="text-2xl font-['Manrope'] text-[#0D2340]">
                {t('doctorPortal')}
              </CardTitle>
              <CardDescription className="text-base">
                {t('enterAccessCode')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleValidate} className="space-y-4">
                <div>
                  <Label>{t('accessCode')}</Label>
                  <Input
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    className="text-center text-2xl font-mono tracking-widest h-14"
                    maxLength={8}
                    data-testid="access-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-14 text-lg bg-[#0D2340]"
                  disabled={loading}
                  data-testid="validate-code-btn"
                >
                  {loading ? t('verifying') : t('viewPatientData')}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-[#FFB300] mt-0.5" />
                  <div className="text-sm text-slate-600">
                    <p className="font-medium">{t('readOnlyAccess')}</p>
                    <p className="mt-1">This portal provides read-only access to patient records. You cannot modify any data.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>

        <div className="text-center p-4">
          <Button variant="link" onClick={() => navigate('/')} className="text-[#0D2340]">
            Patient Login
          </Button>
        </div>
      </div>
    );
  }

  // Validated - Show Patient Data
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="secure-header text-white p-4 no-print">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8" />
            <div>
              <h1 className="text-xl font-bold font-['Manrope']">{t('appName')}</h1>
              <p className="text-xs opacity-80">{t('doctorPortal')} - {t('readOnlyAccess')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleSpeak(t('appTagline'))}
              className="text-white hover:bg-white/20"
            >
              {isSpeaking ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              onClick={handlePrint}
              className="text-white hover:bg-white/20"
              data-testid="print-btn"
            >
              <Printer className="h-5 w-5 mr-2" />
              Print Complete Report
            </Button>
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-white hover:bg-white/20 lang-toggle"
            >
              <Globe className="h-5 w-5 mr-2" />
              {language === 'en' ? 'हिंदी' : 'EN'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Patient Info Card */}
        <Card className="bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white" data-testid="patient-info-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <User className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold font-['Manrope']">{patientData.patient?.name}</h2>
                <p className="opacity-80">{patientData.patient?.email}</p>
                <p className="opacity-80">{patientData.patient?.phone}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-white/20 text-white mb-2">
                  <Clock className="h-3 w-3 mr-1" />
                  Expires: {new Date(patientData.expires_at).toLocaleString()}
                </Badge>
                <p className="text-sm opacity-80">Accessed by: Dr. {patientData.doctor_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Health Data Tabs */}
        <Tabs defaultValue="medical_history" className="w-full">
          <TabsList className="grid grid-cols-5 w-full no-print">
            {patientData.access_sections.includes('medical_history') && (
              <TabsTrigger value="medical_history" data-testid="tab-medical-history">
                <Heart className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{t('medicalHistory')}</span>
              </TabsTrigger>
            )}
            {patientData.access_sections.includes('prescriptions') && (
              <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">
                <Pill className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{t('prescriptions')}</span>
              </TabsTrigger>
            )}
            {patientData.access_sections.includes('reports') && (
              <TabsTrigger value="reports" data-testid="tab-reports">
                <FileText className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{t('reports')}</span>
              </TabsTrigger>
            )}
            {patientData.access_sections.includes('diary') && (
              <TabsTrigger value="diary" data-testid="tab-diary">
                <Calendar className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{t('diary')}</span>
              </TabsTrigger>
            )}
            {patientData.access_sections.includes('referrals') && (
              <TabsTrigger value="referrals" data-testid="tab-referrals">
                <Users className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">{t('referrals')}</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Medical History Tab */}
          <TabsContent value="medical_history" className="mt-4">
            <div className="grid gap-4">
              {patientData.health_data?.medical_history?.length > 0 ? (
                patientData.health_data.medical_history.map((entry, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-lg font-['Manrope']">{entry.condition}</h3>
                        <Badge variant={entry.status === 'ongoing' ? 'destructive' : 'secondary'}>
                          {entry.status}
                        </Badge>
                      </div>
                      <p className="text-slate-600">{entry.description}</p>
                      <div className="mt-2 text-sm text-slate-500">
                        Diagnosed: {entry.diagnosed_date} | Doctor: {entry.doctor_name || 'N/A'}
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">{t('noData')}</p>
              )}
            </div>
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions" className="mt-4">
            <div className="grid gap-4">
              {patientData.health_data?.prescriptions?.length > 0 ? (
                patientData.health_data.prescriptions.map((entry, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg font-['Manrope']">{entry.medication_name}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-xs text-slate-500">{t('dosage')}</span>
                          <p className="font-medium">{entry.dosage}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-xs text-slate-500">{t('frequency')}</span>
                          <p className="font-medium">{entry.frequency}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-xs text-slate-500">{t('doctorName')}</span>
                          <p className="font-medium">{entry.doctor_name}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-xs text-slate-500">{t('startDate')}</span>
                          <p className="font-medium">{entry.start_date}</p>
                        </div>
                      </div>
                      {entry.notes && <p className="mt-2 text-sm text-slate-600">{entry.notes}</p>}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">{t('noData')}</p>
              )}
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4">
            <div className="grid gap-4 md:grid-cols-2">
              {patientData.health_data?.reports?.length > 0 ? (
                patientData.health_data.reports.map((entry, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-[#0D2340]" />
                        <h3 className="font-semibold font-['Manrope']">{entry.report_name}</h3>
                      </div>
                      <Badge variant="outline" className="mb-2">{entry.report_type}</Badge>
                      <p className="text-sm text-slate-500">Date: {entry.report_date}</p>
                      {entry.lab_name && <p className="text-sm text-slate-500">Lab: {entry.lab_name}</p>}
                      {entry.findings && <p className="mt-2 text-slate-600">{entry.findings}</p>}
                      {entry.file_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 w-full"
                          onClick={() => handleDownloadFile(entry.file_id, entry.file_name || `${entry.report_name}.pdf`)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Report
                        </Button>
                      )}
                      {entry.file_path && !entry.file_id && (
                        <div className="mt-3 p-2 bg-slate-50 rounded flex items-center gap-2">
                          <FileImage className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-500">File attached</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8 col-span-2">{t('noData')}</p>
              )}
            </div>
          </TabsContent>

          {/* Diary Tab */}
          <TabsContent value="diary" className="mt-4">
            <div className="grid gap-4">
              {patientData.health_data?.diary?.length > 0 ? (
                patientData.health_data.diary.map((entry, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge>{entry.entry_type.replace('_', ' ')}</Badge>
                        <span className="text-sm text-slate-500">{entry.entry_date}</span>
                      </div>
                      {entry.entry_type === 'blood_pressure' && (
                        <p className="text-2xl font-bold text-[#0D2340]">
                          {entry.data.systolic}/{entry.data.diastolic} mmHg
                          {entry.data.pulse && <span className="text-base font-normal text-slate-500 ml-2">Pulse: {entry.data.pulse} bpm</span>}
                        </p>
                      )}
                      {entry.entry_type === 'food' && (
                        <div>
                          <p className="font-semibold">{entry.data.meal}</p>
                          <p className="text-slate-600">{entry.data.items}</p>
                          {entry.data.calories && <Badge variant="outline" className="mt-1">{entry.data.calories} cal</Badge>}
                        </div>
                      )}
                      {entry.entry_type === 'exercise' && (
                        <div>
                          <p className="font-semibold">{entry.data.activity_type}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline">{entry.data.duration} mins</Badge>
                            {entry.data.calories_burned && <Badge variant="outline">{entry.data.calories_burned} cal burned</Badge>}
                          </div>
                        </div>
                      )}
                      {entry.notes && <p className="mt-2 text-sm text-slate-500">{entry.notes}</p>}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">{t('noData')}</p>
              )}
            </div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="mt-4">
            <div className="grid gap-4">
              {patientData.health_data?.referrals?.length > 0 ? (
                patientData.health_data.referrals.map((entry, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-[#0D2340]">{entry.referring_doctor}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-semibold text-[#0D2340]">{entry.referred_to_doctor}</span>
                        <Badge className="bg-[#FFB300] text-slate-900">{entry.speciality}</Badge>
                      </div>
                      <p className="text-slate-600">{entry.reason}</p>
                      <p className="text-sm text-slate-500 mt-2">Date: {entry.referral_date}</p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">{t('noData')}</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Security Notice */}
        <Card className="bg-slate-50 no-print">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#0D2340]" />
              <div>
                <p className="font-semibold font-['Manrope']">{t('blockchainSecured')}</p>
                <p className="text-sm text-slate-500">{t('tamperProof')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Complete Health Report</DialogTitle>
          </DialogHeader>
          <PrintableHealthReport ref={printRef} patientData={patientData} language={language} />
          <div className="flex gap-2 mt-4 no-print">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)} className="flex-1">
              Close
            </Button>
            <Button onClick={handlePrintReport} className="flex-1 bg-[#0D2340]">
              <Printer className="h-4 w-4 mr-2" />
              Print Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorPortalPage;
